'use client';

import { Tab } from '@/components/tabs/Tab';
import { Tabs } from '@/components/tabs/Tabs';
import { qaAttr } from '@/lib/qa';
import {
  LineToken,
  SurfaceColorToken,
  theme,
  themeValues,
  TypoColorToken,
} from '@vuddy/components/theme';
import { isClientSide, jsonAssignInlineVars } from '@vuddy/utils';
import { findLastIndex, isNil } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as styles from './tabLinks.css';

interface TabLinksProps {
  tabLinks: { id: string; label: string }[];
  backgroundColor?: SurfaceColorToken;
  activeColor?: TypoColorToken;
  activeBorderColor?: LineToken;
  inActiveBorderColor?: LineToken;
  inActiveTextColor?: TypoColorToken;
  activeSectionAnchor?: 'bottom' | 'middle' | 'top';
  qaId?: string;
}

export const SalePageTabLinks = ({
  tabLinks,
  backgroundColor = 'primary',
  activeColor = 'primary',
  activeBorderColor = 'blackPrimary',
  inActiveBorderColor = 'dividerPrimary',
  inActiveTextColor = 'tertiary',
  activeSectionAnchor = 'bottom',
  qaId,
}: TabLinksProps) => {
  const tabContentsRef = useRef<Array<HTMLDivElement | null>>([]);
  const tabContentTopsRef = useRef<number[]>([]);
  const rafIdRef = useRef<number | null>(null);
  const scrollSpySuppressedRef = useRef(false);
  const suppressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const layoutConstantsRef = useRef({
    ctaHeight: 0,
    navHeight: 0,
    tabHeight: 0,
  });

  const syncTabContents = useCallback(() => {
    if (!isClientSide) return;
    tabContentsRef.current = tabLinks.map(({ id }) =>
      document.querySelector<HTMLDivElement>(`#${id}`)
    );
    tabContentTopsRef.current = tabContentsRef.current.map(
      el => el?.offsetTop ?? 0
    );
    const tabLinksElement = document.querySelector<HTMLElement>(
      `.${styles.tabLinks}`
    );
    layoutConstantsRef.current = {
      ctaHeight: parseInt(themeValues.ctaHeight, 10),
      navHeight: parseInt(themeValues.navigationBarHeight, 10),
      tabHeight: tabLinksElement?.offsetHeight ?? 0,
    };
  }, [tabLinks]);

  const calcActiveIndex = useCallback(() => {
    if (!isClientSide || isNil(document.scrollingElement)) return 0;

    if (
      tabContentsRef.current.length !== tabLinks.length ||
      tabContentsRef.current.some(contents => isNil(contents))
    ) {
      syncTabContents();
    }

    const { clientHeight, scrollTop } = document.scrollingElement;
    const { ctaHeight, navHeight, tabHeight } = layoutConstantsRef.current;
    const scrollBottom = scrollTop + clientHeight - ctaHeight;
    const scrollMiddle = scrollTop + (clientHeight - ctaHeight) / 2;
    const scrollTopAnchor = scrollTop + navHeight + tabHeight + 8;
    const scrollAnchor =
      activeSectionAnchor === 'middle'
        ? scrollMiddle
        : activeSectionAnchor === 'top'
          ? scrollTopAnchor
          : scrollBottom;

    const tabContentTops = tabContentTopsRef.current;
    const firstTop = tabContentTops[0] ?? 0;
    // top 모드에서는 scrollAnchor(nav+tab 아래 기준)로 판정해야
    // 첫 번째 섹션 진입 감지가 findLastIndex와 일관된다.
    const firstTabThreshold =
      activeSectionAnchor === 'top' ? scrollAnchor : scrollTop;
    if (firstTabThreshold <= firstTop + 16) {
      return 0;
    }
    return Math.max(
      0,
      findLastIndex(tabContentTops, top => top <= scrollAnchor)
    );
  }, [activeSectionAnchor, syncTabContents, tabLinks.length]);

  const [activeIndex, setActiveIndex] = useState(0);

  const handleTabSelect = useCallback(
    (index: number) => {
      if (!isClientSide) return false;

      const targetId = tabLinks[index]?.id;
      if (isNil(targetId)) return false;

      const target = document.querySelector<HTMLElement>(`#${targetId}`);
      if (isNil(target)) return false;

      const tabLinksElement = document.querySelector<HTMLElement>(
        `.${styles.tabLinks}`
      );
      const navHeight = parseInt(themeValues.navigationBarHeight, 10);
      const tabHeight = tabLinksElement?.offsetHeight ?? 0;
      const scrollTop =
        window.scrollY +
        target.getBoundingClientRect().top -
        navHeight -
        tabHeight;

      const nextHash = `#${targetId}`;
      const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;

      setActiveIndex(index);
      window.history.replaceState(null, '', nextUrl);

      // smooth scroll 중 스크롤 스파이가 이전 탭을 다시 활성화하는 것을 방지
      scrollSpySuppressedRef.current = true;
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = setTimeout(() => {
        scrollSpySuppressedRef.current = false;
      }, 600);

      window.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: 'smooth',
      });

      // 기본 Tab 스크롤 동작을 사용하지 않고, URL hash + 오프셋 스크롤을 직접 적용한다.
      return false;
    },
    [tabLinks]
  );

  useEffect(() => {
    if (!isClientSide || isNil(document.scrollingElement)) return;

    syncTabContents();

    const updateActiveIndex = () => {
      setActiveIndex(prev => {
        const next = calcActiveIndex();
        return prev === next ? prev : next;
      });
    };

    const handleScroll = () => {
      if (!isNil(rafIdRef.current) || scrollSpySuppressedRef.current) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateActiveIndex();
      });
    };

    updateActiveIndex();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', syncTabContents);

    // 콘텐츠 높이 변경(이미지 로딩 등) 시 offsetTop 캐시 갱신
    const resizeObserver = new ResizeObserver(() => {
      tabContentTopsRef.current = tabContentsRef.current.map(
        el => el?.offsetTop ?? 0
      );
    });
    tabContentsRef.current.forEach(el => {
      if (el) resizeObserver.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', syncTabContents);
      resizeObserver.disconnect();
      if (!isNil(rafIdRef.current)) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (suppressTimerRef.current) {
        clearTimeout(suppressTimerRef.current);
      }
    };
  }, [calcActiveIndex, syncTabContents]);

  return (
    <div className={styles.stickyWrapper} {...(qaId ? qaAttr(qaId) : {})}>
      <Tabs
        className={styles.tabLinks}
        style={jsonAssignInlineVars({
          [styles.backgroundColor]: theme.surface[backgroundColor],
        })}
        activeIndex={activeIndex}
        onChangeIndex={setActiveIndex}
        activeColor={activeBorderColor}
        inactiveColor={inActiveBorderColor}>
        {tabLinks.map(({ id, label }, index) => (
          <Tab
            key={id}
            index={index}
            title={label}
            targetId={id}
            onSelect={handleTabSelect}
            backgroundColor={backgroundColor}
            activeColor={activeColor}
            inActiveTextColor={inActiveTextColor}
          />
        ))}
      </Tabs>
    </div>
  );
};
